import React from 'react';
import Tabs, { TabsProps } from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import CcfTabsPanel from './templates/ccf-tabs-panel';
import { CcfTooltip } from '../../core/ccf-tooltip/ccf-tooltip';
import CcfDivider, { DividerOrientation, DividerVariant } from '../ccf-divider/ccf-divider';

export enum Variant {
  FULL_WIDTH = 'fullWidth',
  SCROLLABLE = 'scrollable',
  STANDARD = 'standard',
}

export enum Color {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
}

export interface CcfTabsProps extends TabsProps {
  children?: React.ReactElement[];
  currentTab: number;
  setCurrentTab: (currentTab: number) => void;
  textColor?: Color.PRIMARY | Color.SECONDARY;
  indicatorColor?: Color.PRIMARY | Color.SECONDARY;
  variant: Variant.FULL_WIDTH | Variant.SCROLLABLE | Variant.STANDARD;
  tabClassname?: string;
  bottomBorder?: boolean;
}

/**
 * 
 * @param currentValue - Current selected Tab
 * @param setCurrentTab - function to change the current Tab
 * @param tabClassname - To give custom classes to the Tab Component 
 * @example <CcfTabs /> 
 * @returns CcfTabs Component
 */
export function CcfTabs({
  children,
  currentTab,
  setCurrentTab,
  textColor,
  indicatorColor,
  variant,
  tabClassname,
  classes,
  bottomBorder,
  ...rest
}: CcfTabsProps) {
  /**
   * 
   * @param event - any
   * @param newValue - any
   * @example handleChange(event,newvalue)
   */
  const handleChange = (event:any, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <>
      <section>
        <Tabs
          value={currentTab}
          onChange={handleChange}
          variant={variant}
          scrollButtons={variant === Variant.SCROLLABLE ? true : false}
          indicatorColor={indicatorColor}
          textColor={textColor}
          aria-label="icon tabs example"
          classes={{
            ...classes,
          }}
          {...rest}
        >
          {React.Children.map(children, (child, idx) => {
            const label = child?.props.label;
            const icon =  child?.props.icon;
            const disabled = child?.props.disabled;
            const tabTooltip = child?.props.tooltip || child?.props?.label;

            return <CcfTooltip aria-label={tabTooltip} title={tabTooltip} arrow disableInteractive>
              <Tab disabled={disabled} classes={
                {
                  ...child?.props?.classes,
                }
              }
              id={`agent_appSpace_tabs_${tabTooltip}`} 
              className={tabClassname} icon={icon} label={label} aria-label={label} key={`${label}_${idx}`}
              style={{fontWeight:600}}  />
 
            </CcfTooltip>         })}

          
        </Tabs>
        {bottomBorder && <CcfDivider
          orientation={DividerOrientation.HORIZONTAL}
          variant={DividerVariant.FULLWIDTH}
        /> }
      </section>
      {React.Children.map(children, (child, idx) => {
        return idx === currentTab ? child : null;
      })}
    </>
  );
}

CcfTabs.TabPanel = CcfTabsPanel;
CcfTabs.Color = Color;
CcfTabs.Variant = Variant;

CcfTabs.defaultProps = {
  textColor: 'inherit',
  indicatorColor: 'primary',
  variant: Variant.FULL_WIDTH,
};

export default CcfTabs;
